import React, { useEffect, useState } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { stateAt, isGuestSpot } from '../city/cityMotion';
import { serverNow } from '../services/worldSocket';
import { listWorldObjects } from '../services/worldObjects';
import { interiorFloor, roomSpace } from '../state/interiorFloor';
import { findMarks } from '../city/guestMarks';
import RoomPerson from './RoomPerson';

// RoomGuests — artists spending their free time INSIDE a venue (2026-11-01).
//
// Khari: "when artists arrive at a location/venue, they should gather IN the
// room, if possible. For example, they are at the blackroom now, but all
// standing outside of the building instead of within. It would be more
// realistic if both happened (some outside, some inside)."
//
// The city decides who is in (API city/guestRooms + CityLifeService.assignSpots):
// a stay with `r` = this room on guest spot `g<k>`. The street stops drawing
// them at the door (CityResident) and this draws them standing about in here,
// in their own body, on mark k, saying whether they are free (RoomPerson). A
// tap is the street's: they stop for you and the chat opens with THEM.
//
// THE MARKS, in room space (services/roomSpace), in this order:
//   1. PLACED — ⚙ → Placeables → "Guest marks", stood on and faced like story
//      marks. Placing them is what opens a SPLAT room (the record store), and
//      they replace the found ones anywhere. The server counts them too.
//   2. FOUND — a mesh room's own floor, asked rather than described (as
//      FloorClamp): city/guestMarks, free ground-floor spots clear of walls,
//      bars, tables and machines, picked in twos facing each other.
// While the Placeables tool is open the marks show as numbered ghosts.
//
// Mounted beside RoomStory (inside the room group, outside its scale group): a
// person is sized against the world, not the building's fit.
const found = new Map();   // roomId → marks found in its mesh (kept for the session)

function Ghost({ mark, n, auto }) {
  const color = auto ? '#8fd3ff' : '#9dff7d';
  return (
    <group position={mark.pos} rotation={[0, mark.rotY || 0, 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.006, 0]}>
        <ringGeometry args={[0.16, 0.2, 40]} />
        <meshBasicMaterial color={color} transparent opacity={auto ? 0.5 : 0.85} depthTest={false} side={2} />
      </mesh>
      <mesh position={[0, 0.006, 0.32]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.09, 0.14, 3]} />
        <meshBasicMaterial color={color} transparent opacity={auto ? 0.5 : 0.85} depthTest={false} />
      </mesh>
      <Billboard position={[0, 0.95, 0]}>
        <Text fontSize={0.11} color={color} anchorX="center" anchorY="middle" renderOrder={10}>
          {auto ? `guest ${n} (found)` : `guest ${n}`}
        </Text>
      </Billboard>
    </group>
  );
}

export default function RoomGuests({ roomId }) {
  const room = ROOMS[roomId];
  const editing = useWorldStore((s) => s.adminTool) === 'placeables';
  const liveRows = useWorldStore((s) => s.worldObjects);
  const live = useCityStore((s) => s.live);
  const residents = useCityStore((s) => s.snap?.residents || null);
  const [placed, setPlaced] = useState(null);
  const [auto, setAuto] = useState(() => found.get(roomId) || null);
  const [now, setNow] = useState(() => serverNow());

  // The saved marks (re-read when the tool closes, so a fresh placement counts).
  useEffect(() => {
    if (!roomId || editing) return undefined;
    let dead = false;
    listWorldObjects(roomId, 'guest_mark').then((rows) => { if (!dead) setPlaced(rows || []); }).catch(() => {});
    return () => { dead = true; };
  }, [roomId, editing]);

  // The found marks: once per room per session, when its mesh is up.
  useEffect(() => {
    if (!roomId || !room?.model || found.has(roomId)) return undefined;
    let dead = false; let tries = 0;
    const tick = setInterval(() => {
      const floor = interiorFloor.object; const space = roomSpace.object;
      if (dead || ++tries > 60) { clearInterval(tick); return; }
      if (!floor || !space) return;
      clearInterval(tick);
      findMarks(roomId, room, floor, space).then((m) => {
        found.set(roomId, m);
        console.log(`[guests] ${roomId}: ${m.length} place(s) to stand found on the floor`);
        if (!dead) setAuto(m);
      });
    }, 500);
    return () => { dead = true; clearInterval(tick); };
  }, [roomId, room?.model]);

  useEffect(() => {
    const t = setInterval(() => setNow(serverNow()), 1000);
    return () => clearInterval(t);
  }, []);

  const rows = editing ? (liveRows || []).filter((o) => o.zone_id === roomId && o.kind === 'guest_mark') : placed;
  const own = [...(rows || [])].sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0)).map((o) => ({ pos: o.pos, rotY: o.rotY || 0 }));
  const marks = own.length ? own : (auto || []);

  const here = [];
  if (live && residents?.length && marks.length) {
    const cs = useCityStore.getState();
    for (const r of residents) {
      const st = stateAt(cs.byId[r.avatarId] || r, now, cs.convs);
      if (st?.inside !== roomId || !isGuestSpot(st.spot)) continue;
      const k = Number(st.spot.slice(1));
      // More guests than marks (fewer found than the server allows): beside an earlier one.
      const base = marks[k % marks.length];
      const lap = Math.floor(k / marks.length);
      const mark = lap ? { pos: [base.pos[0] + Math.sin(base.rotY + Math.PI / 2) * 0.6 * lap, base.pos[1] || 0, base.pos[2] + Math.cos(base.rotY + Math.PI / 2) * 0.6 * lap], rotY: base.rotY } : base;
      here.push({ r, mark });
    }
  }
  const light = (room?.light ?? 1) * (room?.model ? 0.45 : 1);

  return (
    <group>
      {editing && marks.map((m, i) => <Ghost key={`g${i}`} mark={m} n={i + 1} auto={!own.length} />)}
      {here.map(({ r, mark }) => (
        <RoomPerson key={r.avatarId} avatarId={r.avatarId} name={r.name} mark={mark} light={light} />
      ))}
    </group>
  );
}
